import {
  CampaignLabel,
  CampaignState,
  CampaignType,
  MessageMedium,
} from 'modules/domains/campaign';
import { AggregationType, ConversionEventState, ConversionState } from 'modules/domains';
import { Nullable } from 'utils/tsHelpers';
import { MediumContent, User } from './GraphQLTemplate';
import { KVAnalyticsMetricResultDefinition } from './GraphQLAnalytics';
import { OffsetDateTime } from './OffsetDateTime';
import { Instant } from './Instant';

export enum ExperimentType {
  EmailBody = 'EmailBody',
  FromNameAndSender = 'FromNameAndSender',
  PreheaderText = 'PreheaderText',
  SendTime = 'SendTime',
  SendTimeOptimization = 'SendTimeOptimization',
  SubjectLine = 'SubjectLine',
  WholeEmail = 'WholeEmail',
}

export enum WinningCriteriaTypeDefinition {
  ConversionEvent = 'ConversionEvent',
  SystemEvent = 'SystemEvent',
}

export enum ExperimentState {
  CANCELLED = 'Cancelled',
  DRAFT = 'Draft',
  ERRORED = 'Errored',
  FINISHED = 'Finished',
  READY = 'Ready',
  RUNNING = 'Running',
  SENDING_TO_REMAINDER = 'SendingToRemainder',
  USER_SELECTED_WINNER = 'UserSelectedWinner',
  WINNER_FOUND = 'WinnerFound',
}

export type Experiment = {
  id: number;
  campaign: ExperimentCampaignDefinition;
  name: string;
  type: ExperimentType;
  createdAt: Instant;
  createdBy: User;
  modifiedAt: Instant;
  startedAt: Instant;
  endedAt: Instant;
  exploitationStartedAt: Instant;
  testDurationInMinutes: number;
  testSizePercentage: number;
  state: ExperimentState;
  evenlySplitVariations: boolean;
  autoSelectWinner: boolean;
  winningCriteria: ExperimentWinningCriteriaDefinition;
  variations: ExperimentVariant[];
  holdout: ExperimentHoldoutDefinition;
  testSendsPerVariation: Nullable<number>;
  attributionPeriodHours: Nullable<number>;
};

export type GroupPercentHoldoutDefinition = {
  percent: number;
  holdoutUserCount: Nullable<number>;
  experimentWinningCriteria: Nullable<number>;
  metrics: KVAnalyticsMetricResultDefinition[];
};

export type ExperimentHoldoutDefinition = Nullable<GroupPercentHoldoutDefinition>;

export type ExplicitlySet = Record<string, never>;
export type LastMessaged = {
  periodHours: number;
};
export type AttributionModeDefinition = ExplicitlySet | LastMessaged;

export type ExperimentCampaignConversionEvent = {
  name: string;
  updatedAt?: Instant;
  state: ConversionEventState;
  attributionMode: AttributionModeDefinition;
};

export type ExperimentCampaignConversion = {
  id: number;
  name: string;
  events: ExperimentCampaignConversionEvent[];
  projectId: number;
  createdById?: string;
  createdAt: Instant;
  updatedById?: string;
  updatedAt: Instant;
  description?: string;
  aggregationType: AggregationType;
  state: ConversionState;
  count?: number;
  rate?: number;
  campaignCount?: number;
  experimentCount?: number;
};

type ExperimentCampaignList = {
  id: number;
};

export type ExperimentCampaignDefinition = {
  id: number;
  name: string;
  createdAt: OffsetDateTime;
  type: CampaignType;
  messageMedium: MessageMedium;
  sendTimeUseRecipientTimeZones: boolean;
  labels: CampaignLabel[];
  state: CampaignState;
  conversions: ExperimentCampaignConversion[];
  lists: ExperimentCampaignList[];
};

export type ExperimentWinningCriteriaDefinition = {
  type: WinningCriteriaTypeDefinition;
  name: string;
};

export type ExperimentVariant = {
  id: number;
  messageMedium: MessageMedium;
  name: string;
  content: MediumContent;
  templateThumbnailUrl: string;
  sendTime: Nullable<string>;
  experimentId: number;
  control: boolean;
  winner: boolean;
  /** Deprecated */
  contentId: number;
  /** Deprecated */
  templateId: number;
  experimentSends: number;
  experimentWinningCriteria: number;
  metrics: KVAnalyticsMetricResultDefinition[];
};
